import { ApiError, activateNicheTemplate, getLocationNicheConfig } from '@jq4race/api-client';
import { type TFunction, useTranslation } from '@jq4race/i18n';
import type { NicheConfigResponse, OrgNiche } from '@jq4race/shared';
import { ORG_NICHE_OPTIONS } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDate } from '../../../../shared/format/format-date';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// ── Types ─────────────────────────────────────────────────────────────────

type NicheOption = OrgNiche;

// ── Helpers ──────────────────────────────────────────────────────────────────

function nicheLabel(t: TFunction, niche: NicheOption): string {
  return enumLabel(t, 'niche', niche);
}

// ── Component ─────────────────────────────────────────────────────────────

export function NicheConfigPage() {
  const { t, i18n } = useTranslation('location-niche-config');
  const { locationId } = useParams({ strict: false });

  const [existingConfig, setExistingConfig] = useState<NicheConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedNiche, setSelectedNiche] = useState<NicheOption>('restaurant');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const pendingNicheRef = useRef<NicheOption | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const response = await getLocationNicheConfig(locationId);
      if (response.activated) {
        setExistingConfig(response);
        setSelectedNiche(response.niche);
      } else {
        setExistingConfig(null);
      }
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner.
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  async function doActivate(niche: NicheOption) {
    if (!locationId) return;
    setSubmitting(true);
    try {
      const saved = await activateNicheTemplate(locationId, { niche });
      setExistingConfig(saved);
      setSelectedNiche(saved.niche);
      setSuccess(t('feedback.activateSuccess', { niche: nicheLabel(t, niche) }));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.activateError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.unexpectedError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Warn if a config already exists (overwrite confirmation — Scenario 9 UI warning)
    if (existingConfig) {
      pendingNicheRef.current = selectedNiche;
      setShowConfirm(true);
    } else {
      void doActivate(selectedNiche);
    }
  }

  function handleConfirm() {
    setShowConfirm(false);
    const niche = pendingNicheRef.current;
    if (niche) {
      void doActivate(niche);
    }
  }

  function handleCancelConfirm() {
    setShowConfirm(false);
    pendingNicheRef.current = null;
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <div className="space-y-6">
              {existingConfig && (
                <div className="rounded-sm border border-jq-sand/50 bg-jq-surface-1 px-4 py-3 text-[13px] text-jq-graphite">
                  <span className="font-medium">{t('currentTemplate.label')}</span>{' '}
                  {t('currentTemplate.details', {
                    niche: nicheLabel(t, existingConfig.niche),
                    version: existingConfig.templateVersion,
                    date: formatDate(existingConfig.templateActivatedAt, i18n.language),
                  })}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <fieldset className="space-y-2">
                  <legend className="text-[13px] font-medium text-jq-graphite">
                    {t('form.selectLegend')}
                  </legend>
                  <div className="flex flex-wrap gap-4">
                    {ORG_NICHE_OPTIONS.map(({ value: niche }) => (
                      <label
                        key={niche}
                        className="flex cursor-pointer items-center gap-2 text-[13px] text-jq-graphite"
                      >
                        <input
                          type="radio"
                          name="niche"
                          value={niche}
                          checked={selectedNiche === niche}
                          onChange={() => setSelectedNiche(niche)}
                          className="accent-jq-primary"
                        />
                        {enumLabel(t, 'niche', niche)}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <Button type="submit" disabled={submitting}>
                  {submitting ? t('form.activating') : t('form.activate')}
                </Button>
              </form>
            </div>
          )}
        </CardContent>
      </Card>

      {showConfirm && (
        <OverwriteConfirmDialog
          existingNiche={existingConfig?.niche ?? 'restaurant'}
          newNiche={selectedNiche}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
        />
      )}
    </div>
  );
}

// ── Overwrite confirm dialog ───────────────────────────────────────────────

interface OverwriteConfirmDialogProps {
  existingNiche: NicheOption;
  newNiche: NicheOption;
  onConfirm: () => void;
  onCancel: () => void;
}

function OverwriteConfirmDialog({
  existingNiche,
  newNiche,
  onConfirm,
  onCancel,
}: OverwriteConfirmDialogProps) {
  const { t } = useTranslation('location-niche-config');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="overwrite-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="mx-4 w-full max-w-sm rounded-md border border-jq-sand/60 bg-jq-surface-0 p-6 shadow-lg">
        <div className="mb-4 flex items-start gap-3">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-jq-warning" strokeWidth={1.75} />
          <div>
            <h2 id="overwrite-dialog-title" className="text-[14px] font-semibold text-jq-graphite">
              {t('dialog.title')}
            </h2>
            <p className="mt-1 text-[13px] text-jq-graphite/80">
              {t('dialog.description', {
                existingNiche: nicheLabel(t, existingNiche),
                newNiche: nicheLabel(t, newNiche),
              })}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" type="button" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" onClick={onConfirm}>
            {t('dialog.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-niche-config');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
